import { useCallback } from "react";
import { useAuth, useAuthConfig, useAuthStatus } from "../auth";

type HeaderMap = Record<string, string>;

function withoutAuthorization(headers: HeaderMap): HeaderMap {
  return Object.fromEntries(Object.entries(headers).filter(([name]) => name.toLowerCase() !== "authorization"));
}

function toHeaderMap(headers: HeadersInit | undefined): HeaderMap {
  if (!headers) return {};
  if (Array.isArray(headers)) return Object.fromEntries(headers);
  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    const map: HeaderMap = {};
    headers.forEach((value, key) => {
      map[key] = value;
    });
    return map;
  }
  return { ...(headers as HeaderMap) };
}

/** The API still answers 401 after a token refresh; a login redirect has been started. */
export class UnauthorizedError extends Error {
  constructor() {
    super("Admin API answered 401: not signed in");
    this.name = "UnauthorizedError";
  }
}

/** The API refused the signed-in user (403): no admin app role for this operation. */
export class ForbiddenError extends Error {
  constructor() {
    super("Admin API answered 403: no admin role");
    this.name = "ForbiddenError";
  }
}

function isAllowed(url: string, apiOrigins: string[]): boolean {
  try {
    return apiOrigins.includes(new URL(url, window.location.href).origin);
  } catch {
    return false;
  }
}

const UNAUTHORIZED = 401;
const FORBIDDEN = 403;

export type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

/** fetch for the admin API: adds the bearer token of the signed-in user, only for the configured API origins. */
export function useAuthFetch(): AuthFetch {
  const { getAccessTokenSilently, startAutoLogin } = useAuth();
  const { apiOrigins } = useAuthConfig();
  const { reportForbidden } = useAuthStatus();

  return useCallback<AuthFetch>(
    async (url, init = {}) => {
      if (!isAllowed(url, apiOrigins)) {
        // Not an admin API origin: never attach (or forward) a bearer token.
        return fetch(url, { ...init, headers: withoutAuthorization(toHeaderMap(init.headers)) });
      }

      const send = async (forceRefresh: boolean) => {
        const token = await getAccessTokenSilently({ forceRefresh });
        const headers = { ...withoutAuthorization(toHeaderMap(init.headers)), Authorization: `Bearer ${token}` };
        return fetch(url, { ...init, headers });
      };

      const checked = (res: Response) => {
        if (res.status === FORBIDDEN) {
          reportForbidden();
          throw new ForbiddenError();
        }
        return res;
      };

      const first = await send(false);
      if (first.status !== UNAUTHORIZED) return checked(first);
      const retry = await send(true);
      if (retry.status !== UNAUTHORIZED) return checked(retry);
      await startAutoLogin();
      throw new UnauthorizedError();
    },
    [getAccessTokenSilently, startAutoLogin, apiOrigins, reportForbidden],
  );
}
